import { useEffect, useState } from "react";
import StatCard from "../components/StatCard";
import CustomBarChart from "../components/CustomBarChart";
// import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const Sustainability = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [mapData, setMapData] = useState([]);

  useEffect(() => {
    const storedData = localStorage.getItem("sustainabilityData");
    setMapData(JSON.parse(storedData) || []);
    if (storedData) {
      const parsedData = JSON.parse(storedData); // نحولها لمصفوفة فعلاً

      const sumKey = (arr, key) =>
        arr.reduce((sum, item) => sum + Number(item[key] || 0), 0);

      setData({
        solar_capacity_mw: sumKey(parsedData, "solar_capacity_mw"),
        sustainable_transport_index: {
          public_transport_share_percent: sumKey(
            parsedData.map((item) => item.sustainable_transport_index || {}),
            "public_transport_share_percent"
          ),
          bike_lanes_km: sumKey(
            parsedData.map((item) => item.sustainable_transport_index || {}),
            "bike_lanes_km"
          ),
          pedestrian_zones_km: sumKey(
            parsedData.map((item) => item.sustainable_transport_index || {}),
            "pedestrian_zones_km"
          ),
        },
        green_infra_coverage_percent: {
          parks: sumKey(
            parsedData.map((item) => item.green_infra_coverage_percent || {}),
            "parks"
          ),
          green_roofs: sumKey(
            parsedData.map((item) => item.green_infra_coverage_percent || {}),
            "green_roofs"
          ),
          urban_forests: sumKey(
            parsedData.map((item) => item.green_infra_coverage_percent || {}),
            "urban_forests"
          ),
        },
        waste_to_energy_mwh_per_year: sumKey(
          parsedData,
          "waste_to_energy_mwh_per_year"
        ),
        climate_risk_index: sumKey(parsedData, "climate_risk_index"),
        circular_economy_score_percent: sumKey(
          parsedData,
          "circular_economy_score_percent"
        ),
        water_energy_nexus_efficiency: {
          energy_per_m3_water_kwh: sumKey(
            parsedData.map((item) => item.water_energy_nexus_efficiency || {}),
            "energy_per_m3_water_kwh"
          ),
        },
        carbon_neutrality_progress_percent: sumKey(
          parsedData,
          "carbon_neutrality_progress_percent"
        ),
      });
    }

    setTimeout(() => setShowSplash(false), 200);
  }, []);

  // if (showSplash || !data) return <SplashScreen />;

  const transportData = [
    {
      label: t("public_transport_ratio"),
      value:
        data?.sustainable_transport_index?.public_transport_share_percent ?? 0,
    },
    {
      label: t("bike_walk_tracks"),
      value: data?.sustainable_transport_index?.bike_lanes_km ?? 0,
    },
    {
      label: t("pedestrian_zones"),
      value: data?.sustainable_transport_index?.pedestrian_zones_km ?? 0,
    },
  ];

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  return (
    <>
      <Helmet>
        <title>{t("sustainability")} | {t("dashboard")}</title>
      </Helmet>

      <div
        className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden"
        
      >
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("sustainability")}
          </h1>

          {/* ComboBox لتحديد القسم */}
          <div className="flex flex-col sm:flex-row gap-2 text-start w-full justify-between items-center">
            <select
              id="category"
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
            >
              <option value="all">{t("all")}</option>
              <option value="energy_environment">{t("energy_environment")}</option>
              <option value="sustainable_transport">{t("sustainable_transport")}</option>
              <option value="green_infrastructure">
                {t("green_infrastructure")}
              </option>
              <option value="map">{t("map_coordinates")}</option>
            </select>

            <button
              onClick={() => navigate("/SustainabilityForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold mt-2 sm:mt-0"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* المحتوى النصفي: 1/3 */}
          <div className="col-span-1 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4" dir="ltr">
            {/* هنا تحط المحتوى بتاعك، زي الأقسام اللي انت كتبتها */}
            {(selectedCategory === "all" ||
              selectedCategory === "energy_environment") && (
              <section className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">{t("energy_environment")}</h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
                  <StatCard
                    title={t("solar_capacity_mw")}
                    value={data?.solar_capacity_mw ?? 0}
                  />
                  <StatCard
                    title={t("waste_to_energy")}
                    value={data?.waste_to_energy_mwh_per_year ?? 0}
                  />
                  <StatCard
                    title={t("climate_risk_index")}
                    value={data?.climate_risk_index ?? 0}
                  />
                  <StatCard
                    title={t("water_energy_efficiency")}
                    value={
                      data?.water_energy_nexus_efficiency
                        ?.energy_per_m3_water_kwh ?? 0
                    }
                  />
                  <StatCard
                    title={t("carbon_neutrality_progress")}
                    value={
                      data?.carbon_neutrality_progress_percent ? data.carbon_neutrality_progress_percent + "%" : "0%"
                    }
                  />
                  <StatCard
                    title={t("circular_economy_index")}
                    value={data?.circular_economy_score_percent ? data.circular_economy_score_percent + "%" : "0%"}
                  />
                </div>
              </section>
            )}

            {(selectedCategory === "all" ||
              selectedCategory === "sustainable_transport") && (
              <section className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">{t("sustainable_transport")}</h2>
                <CustomBarChart
                  title={t("sustainable_transport")}
                  data={transportData}
                  xKey="label"
                  barKey="value"
                  barColor="#3b82f6"
                />
              </section>
            )}

            {(selectedCategory === "all" ||
              selectedCategory === "green_infrastructure") && (
              <section className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">
                  {t("green_infrastructure")}
                </h2>
                <StatCard
                  title={t("green_infrastructure")}
                  value={
                    data?.green_infrastructure_coverage_percent
                      ? `${data.green_infrastructure_coverage_percent}%`
                      : "0%"
                  }
                />
              </section>
            )}
          </div>

          {/* جزء الخريطة 2/3 */}
          <div
            className={`md:col-span-2 relative h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20 overflow-hidden`}
          >
            {(selectedCategory === "all" ||
              selectedCategory === "map") && (
              <div className="absolute inset-0">
                <MapDash initialData={mapData} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sustainability;


